/**
 * Transform API response to design structure
 * Maps API task data to the format expected by the new Tasks Dashboard UI
 *
 * @param {Object} apiTask - Task data from API
 * @param {Boolean} isDetailMode - Whether this is from detail mode (full data) or list mode (minimal data)
 */

export const transformTaskData = (apiTask, isDetailMode = false) => {
  // Format location from address
  const formatLocation = (address) => {
    // LIST MODE: address is a pre-formatted string
    if (typeof address === 'string') {
      return address || null;
    }

    // DETAIL MODE: address is an object with full details
    if (address && typeof address === 'object' && !Array.isArray(address)) {
      const parts = [];
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      return parts.length > 0 ? parts.join(", ") : null;
    }

    // LEGACY: address is an array (old format)
    if (!address || !Array.isArray(address) || address.length === 0) return null;
    const addr = address[0];
    const parts = [];
    if (addr.district__name) parts.push(addr.district__name);
    if (addr.city__city_name) parts.push(addr.city__city_name);
    if (addr.state__state_name) parts.push(addr.state__state_name);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  // Determine status from task data
  const determineStatus = (task) => {
    if (task.status) return task.status;
    // Additional logic can be added here to determine status from other fields
    return "Open";
  };

  // Format schedule date/time from date_time field
  const formatScheduleDate = (schedule) => {
    if (!schedule || !schedule.date_time) return null;
    // Extract date from ISO datetime string
    const dateTime = new Date(schedule.date_time);
    return dateTime.toISOString().split("T")[0]; // Returns YYYY-MM-DD
  };

  const formatScheduleTime = (schedule) => {
    if (!schedule || !schedule.date_time) return null;
    // Extract time from ISO datetime string
    const dateTime = new Date(schedule.date_time);
    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Extract partner name - handle object format (actual API structure)
  const getPartnerName = (partner) => {
    if (!partner || partner === "") return null;
    // Partner is an object with name field
    return partner.name || null;
  };

  // Extract partner info
  const getPartnerInfo = (partner) => {
    if (!partner || partner === "") return null;
    // Partner is an object - include all fields needed for RevisitModal
    return {
      id: partner.id,
      name: partner.name || partner.partner_name,
      phone: partner.phone,
      email: partner.email,
      finalDeliveryPrice: partner.final_delivery_price,
      hrvid: partner.hrvid || partner.HRVID,
      company_name: partner.company_name || partner.contact?.company_name,
      rating: partner.rating,
      // Include contact object if it exists (for compatibility with RevisitModal's extractPartnerInfo)
      contact: partner.contact || (partner.name ? {
        partner_name: partner.name,
        company_name: partner.company_name,
        phone: partner.phone,
        email: partner.email,
      } : null),
    };
  };

  // Handle work_order and visit - can be empty string or object
  const getWorkOrder = (workOrder) => {
    if (!workOrder || workOrder === "") return null;
    return workOrder;
  };

  const getVisit = (visit) => {
    if (!visit || visit === "") return null;
    return visit;
  };

  // Get visit info from ticket_visit_info array (most recent non-cancelled visit)
  const getLatestVisitInfo = (ticketVisitInfo) => {
    if (
      !ticketVisitInfo ||
      !Array.isArray(ticketVisitInfo) ||
      ticketVisitInfo.length === 0
    ) {
      return null;
    }
    // Get the most recent non-cancelled visit, or most recent visit if all are cancelled
    const nonCancelledVisits = ticketVisitInfo.filter((v) => !v.is_cancelled);
    const visitToUse =
      nonCancelledVisits.length > 0
        ? nonCancelledVisits[nonCancelledVisits.length - 1]
        : ticketVisitInfo[ticketVisitInfo.length - 1];

    return {
      id: visitToUse.id,
      visitNumber: visitToUse.visit_number,
      startTime: visitToUse.start_time,
      endTime: visitToUse.end_time,
      isCancelled: visitToUse.is_cancelled,
      isFailure: visitToUse.is_failure,
      comment: visitToUse.comment,
      responsible: visitToUse.responsible,
      note: visitToUse.note,
      revisitStatus: visitToUse.revisit_status,
      engineerId: visitToUse.engineer_id,
      engineerInfo: visitToUse.engineer_info,
      additionalEngineerInfo: visitToUse.additional_engineer_info,
    };
  };

  // Base fields - always present in both LIST and DETAIL modes
  const baseFields = {
    id: apiTask.id?.toString() || apiTask.ticket?.uuid || "",
    ticketId: apiTask.ticket?.number || "N/A",
    ticketUuid: apiTask.ticket?.uuid || null,
    taskType: apiTask.name || "Unknown Task",
    taskTypeCategory: apiTask.task_type || "other",
    projectName: apiTask.project?.name || "N/A",
    projectId: apiTask.project?.id || null,
    partnerName: getPartnerName(apiTask.partner),
    status: determineStatus(apiTask),
    scheduleDate: formatScheduleDate(apiTask.schedule),
    scheduleTime: formatScheduleTime(apiTask.schedule),
    scheduledDateTime: apiTask.schedule?.date_time || null, // Full ISO datetime for countdown
    isTimeBounded: !!(apiTask.schedule && apiTask.schedule.date_time),
    location: formatLocation(apiTask.address),
  };

  // LIST MODE - Return minimal data for task cards
  if (!isDetailMode) {
    return {
      ...baseFields,
      // Keep original data for reference (lightweight)
      originalData: apiTask,
    };
  }

  // DETAIL MODE - Return complete data for task modal
  return {
    ...baseFields,
    // Partner information (full)
    partnerInfo: getPartnerInfo(apiTask.partner),
    // Ticket information (full)
    ticket: apiTask.ticket || null,
    clientTicketIdentifier: apiTask.ticket?.client_ticket_identifier || null,
    clientTasks: apiTask.ticket?.client_tasks || [],
    createdBy: apiTask.ticket?.created_by || null,
    company: apiTask.ticket?.client_ticket_identifier || null,
    // Documents
    documentsRequired: apiTask.required_documents || apiTask.documents_required || [],
    hardCopyRequired: apiTask.hard_copy_required || [],
    uploadedDocuments: apiTask.uploaded_documents || {},
    documentStatus: apiTask.document_status || null,
    // Work Orders
    workOrder: getWorkOrder(apiTask.work_order),
    work_orders: apiTask.work_orders || null,
    work_order: apiTask.work_order || [],
    // Visit information
    visit: getVisit(apiTask.visit),
    visit_info: apiTask.visit_info || null,
    ticketVisitInfo: apiTask.ticket_visit_info || [],
    latestVisitInfo: getLatestVisitInfo(apiTask.ticket_visit_info),
    visitData: apiTask.visit_data || null,
    // Pricing
    estimatedDeliveryPrice: apiTask.estimated_delivery_price || 0,
    estimatedRevisitPrice: apiTask.estimated_revisit_price || 0,
    currency: apiTask.currency || "Indian Rupee",
    price: apiTask.price || 0,
    finalRevisitPrice: apiTask.final_revisit_price || 0,
    aboveBudgetReason: apiTask.above_budget_reason || null,
    revisitAboveBudgetReason: apiTask.revisit_above_budget_reason || null,
    pendingActionsPrice: apiTask.pending_actions_price || null,
    // Customer data
    customerData: apiTask.customer_data || null,
    // SOW information
    sowSubTypeInfo: apiTask.sow_sub_type_info || null,
    sowSubTypeId:
      apiTask.sow_sub_type_id || apiTask.sow_sub_type_info?.id || null,
    // Status flags
    priority: apiTask.priority || null,
    created: apiTask.created || null,
    atleastOneUploaded: apiTask.atleast_one_uploaded || false,
    workOrderAssigned: apiTask.work_order_assigned || false,
    isRejected: apiTask.is_rejected || false,
    isTicketClosed: apiTask.is_ticket_closed || false,
    hasPendingApproval: apiTask.has_pending_approval || false,
    // Partner allocation
    allocated_partners: apiTask.allocated_partners || [],
    payablePartner: apiTask.payable_partner || null,
    // Engineer information
    engineer_info: apiTask.engineer_info || {},
    // Work order issue
    issue_wo: apiTask.issue_wo || null,
    // Schedule
    schedule: apiTask.schedule || null,
    // Address (full object in detail mode)
    address: apiTask.address || null,
    // Rejected work order data
    rejectedFor: apiTask.rejected_for || null,
    rejectedWorkorderData: apiTask.rejected_workorder_data || null,
    // Keep original data for reference
    originalData: apiTask,
  };
};

/**
 * Group tasks by time-bounded status
 * Prioritizes tasks by schedule: Overdue > Active Today > Upcoming (next 2 days) > Other
 */
export const groupTasksByTimeStatus = (tasks) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  // Set to end of day for comparison
  dayAfterTomorrow.setHours(23, 59, 59, 999);

  const grouped = {
    active: [],
    overdue: [],
    upcoming: [],
    other: [],
  };

  tasks.forEach((task) => {
    if (task.isTimeBounded && task.scheduleDate) {
      const scheduleDate = new Date(task.scheduleDate);
      // Normalize schedule date to start of day for comparison
      const scheduleDateOnly = new Date(
        scheduleDate.getFullYear(),
        scheduleDate.getMonth(),
        scheduleDate.getDate()
      );

      // Check if overdue (past today and not closed/resolved)
      if (
        scheduleDateOnly < today &&
        !["closed", "resolved"].includes(task.status.toLowerCase())
      ) {
        grouped.overdue.push(task);
      }
      // Check if active today
      else if (scheduleDateOnly.getTime() === today.getTime()) {
        grouped.active.push(task);
      }
      // Check if upcoming (tomorrow or day after tomorrow)
      else if (
        scheduleDateOnly >= tomorrow &&
        scheduleDateOnly <= dayAfterTomorrow
      ) {
        grouped.upcoming.push(task);
      }
      // Other time-bounded tasks (beyond next 2 days)
      else {
        grouped.other.push(task);
      }
    } else {
      // Non-time-bounded tasks
      grouped.other.push(task);
    }
  });

  // Sort Active Today tasks:
  // 1. Overdue tasks first (most overdue = longest time past schedule = first)
  // 2. Then upcoming tasks (closest to schedule = least time remaining = first)
  // 3. Tasks with most time available (furthest in future) = last
  grouped.active.sort((a, b) => {
    if (!a.scheduledDateTime || !b.scheduledDateTime) {
      // Tasks without scheduled time go to the end
      if (!a.scheduledDateTime && !b.scheduledDateTime) return 0;
      if (!a.scheduledDateTime) return 1;
      if (!b.scheduledDateTime) return -1;
    }

    const now = new Date();
    const aTime = new Date(a.scheduledDateTime);
    const bTime = new Date(b.scheduledDateTime);

    // Calculate time difference from now (negative = overdue, positive = upcoming)
    const aDiff = aTime.getTime() - now.getTime();
    const bDiff = bTime.getTime() - now.getTime();

    // If both are overdue (negative), sort by most overdue first (largest negative = first)
    if (aDiff < 0 && bDiff < 0) {
      return aDiff - bDiff; // Most negative (most overdue) first
    }
    // If both are upcoming (positive), sort by closest first (smallest positive = first)
    if (aDiff > 0 && bDiff > 0) {
      return aDiff - bDiff; // Smallest positive (closest) first
    }
    // If one is overdue and one is upcoming, overdue comes first
    if (aDiff < 0) return -1; // a is overdue, comes first
    if (bDiff < 0) return 1; // b is overdue, comes first

    return 0;
  });

  return grouped;
};

/**
 * Helper function to determine scheduling status of a task
 */
export const getSchedulingStatus = (task) => {
  const taskType = task.taskType.toLowerCase();
  const schedulingTasks = ["allocate partner", "schedule ticket"];

  if (schedulingTasks.includes(taskType) && !task.scheduleDate) {
    return "to_schedule";
  }
  if (task.scheduleDate) {
    if (task.partnerName) {
      const status = task.status.toLowerCase();
      if (status === "scheduled") return "notified";
      if (status === "accepted") return "accepted";
      if (status === "declined") return "declined";
    }
    return "scheduled";
  }
  return null;
};
